/**
 * SleepyCat first-party attribution capture v2.1.0
 *
 * Changelog from v2.0.0:
 *  FIX A  – Pre-checkout forced sync now uses `keepalive: true` so the POST
 *            survives the checkout navigation (a plain fetch is cancelled on
 *            unload, which silently defeated the v2.0 pre-checkout guard).
 *  FIX B  – In-flight guard prevents duplicate concurrent /cart/update.js POSTs
 *            (idle sync + cartUpdate could both fire before the hash was written).
 *  ADD C  – ALWAYS-ON LANDING CAPTURE (classification-independent):
 *            Snapshots the session-entry page exactly once per session —
 *            full path, raw query string, every query parameter, and referrer —
 *            regardless of whether the traffic can be classified.
 *            Surfaces:
 *              landing_path / first_landing_path      (entry path, own budget)
 *              landing_query / first_landing_query     (raw query string)
 *              qp_<name>                                (every UNKNOWN query param,
 *                                                        e.g. affiliate subid, coupon,
 *                                                        custom click ref — capped,
 *                                                        sensitive keys denylisted)
 *            This guarantees that even a `direct` / `referral` / `unknown` visit
 *            still records WHERE the user landed and THROUGH WHAT params.
 *
 * Changelog from v1.x (carried over):
 *  FIX 1  – Traffic type UTM-aware: click IDs → utm_medium → utm_source → referrer.
 *  FIX 2  – Internal referrer guard.
 *  FIX 3  – Hash-based smart sync.
 *  ADD 4  – 7 new click IDs (msclkid, ttclid, twclid, li_fat_id, ScCid, irclickid, mc_eid).
 *  ADD 5  – Pre-checkout sync guard.
 *  ADD 6  – UTM case normalisation.
 *  ADD 7  – Timestamps in cart attributes.
 *  ADD 8  – 90-day cookie lifetime.
 *  ADD 9  – Visit counter / customer_type.
 *  FIX 11 – Dynamic own-hostname detection.
 *  ADD 12 – India-specific channels.
 *  ADD 13 – Razorpay Magic Checkout hook.
 *
 * Storage layering:
 *   - Durable attribution (UTM/click/referrer touches): cookie + localStorage.
 *   - Rich landing detail (qp_*, landing_query) + visit counter: localStorage only,
 *     to keep request cookie headers lean. `last_landing_page`/`first_landing_page`
 *     remain cookie-backed as the always-available fallback.
 *
 * @version 2.1.0
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;

  // ── 1. CONSTANTS ─────────────────────────────────────────────────────────────

  var ATTR_KEYS = [
    // Standard UTM
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    // Google Ads (wbraid/gbraid = privacy-safe iOS/Safari replacements for gclid)
    'gclid', 'wbraid', 'gbraid',
    // Meta Ads
    'fbclid',
    // Microsoft / Bing Ads
    'msclkid',
    // TikTok Ads
    'ttclid',
    // Twitter / X Ads
    'twclid',
    // LinkedIn Ads
    'li_fat_id',
    // Snapchat Ads
    'ScCid',
    // Impact / Radius affiliate network
    'irclickid',
    // Mailchimp / email click ID
    'mc_eid'
    // ShareChat / Moj: param name varies — uncomment when confirmed with your campaign setup
    // 'sc_click_id'
  ];

  // Cookie / storage key names
  var C_FIRST  = 'sc_attrib_first';     // first-touch UTM/click-ID touch (cookie + LS)
  var C_LAST   = 'sc_attrib_last';      // last-touch  UTM/click-ID touch (cookie + LS)
  var C_RF_L   = 'sc_attrib_ref_last';  // last-touch  always-on referrer (cookie + LS)
  var C_RF_F   = 'sc_attrib_ref_first'; // first-touch always-on referrer (cookie + LS)
  var C_VISITS = 'sc_attrib_visits';    // visit counter (localStorage only)
  var C_LAND_L = 'sc_landing_last';     // session-entry landing snapshot (localStorage only)
  var C_LAND_F = 'sc_landing_first';    // first-ever landing snapshot   (localStorage only)
  var S_HASH   = 'sc_attrib_hash';      // hash of last-synced attrs (sessionStorage)
  var S_SESS   = 'sc_session_active';   // new-session marker for visit counter (sessionStorage)
  var S_LAND   = 'sc_landing_done';     // landing already snapshotted this session (sessionStorage)

  var MAX_AGE  = 60 * 60 * 24 * 90;     // 90-day cookie lifetime (GA4 default window)
  var MAX_LEN  = 255;                    // self-imposed cap for short fixed fields (utm_*, click IDs,
                                         // referrer, paths). Shopify documents NO hard length limit
                                         // for cart attribute VALUES (only the cart NOTE is capped, at 5000).
  var MAX_QUERY_VAL = 2048;             // query-derived values (qp_*, landing_query) are NOT capped at 255.
                                         // Generous ceiling kept only to avoid a pathological URL bloating
                                         // the cart/order; raise or remove freely — no Shopify limit applies.
  var MAX_QP_KEY = 40;                  // qp_<name> key length cap (sanity; see checkout-extension caveat in notes)
  var MAX_QP   = 12;                     // max UNKNOWN query params surfaced as qp_* attrs

  // Dynamic — works on staging, headless, and production without code changes
  var OWN_HOST = location.hostname.replace(/^www\./, '').toLowerCase();

  // Never write these to an order attribute — they are PII / secrets and order
  // attributes are visible in Shopify admin, Flow, and the Sheets pipeline.
  var SENSITIVE_QP = /(pass|pwd|token|auth|secret|sig|signature|jwt|session|sid|otp|email|e_?mail|phone|mobile|cvv|card|account|access|apikey|api_key)/i;

  // ── 2. STORAGE HELPERS ───────────────────────────────────────────────────────

  function setCookie(name, val) {
    try {
      document.cookie = name + '=' + encodeURIComponent(val) +
        '; Max-Age=' + MAX_AGE + '; Path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function lsGet(k)    { try { return localStorage.getItem(k);   } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v);       } catch (e) {}               }
  function ssGet(k)    { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v);     } catch (e) {}               }

  function readStore(k)     { return getCookie(k) || lsGet(k); }
  function writeStore(k, v) { setCookie(k, v); lsSet(k, v); }

  function parse(json) {
    if (!json) return null;
    try { return JSON.parse(json); } catch (e) { return null; }
  }

  // Normalise ATTR_KEYS / param names to the storage/cart key format.
  // ScCid → sccid, li_fat_id → li_fat_id, etc.
  function normKey(k) {
    return String(k).toLowerCase().replace(/[^a-z0-9_]/g, '_');
  }

  // Precompute once — ATTR_KEYS is static. Avoids re-normalising the 16 keys on
  // every buildAttributes()/emitUnknownParams() call (these run on each cart sync
  // and on every theme cartUpdate event).
  var NORM_KEYS  = [];
  var KNOWN_KEYS = {};
  for (var _k = 0; _k < ATTR_KEYS.length; _k++) {
    var _nk = normKey(ATTR_KEYS[_k]);
    NORM_KEYS.push(_nk);
    KNOWN_KEYS[_nk] = 1;
  }

  // ── 3. DJB2 HASH (change detection only — not security) ──────────────────────

  function hashStr(str) {
    var h = 5381, i = str.length;
    while (i) h = ((h << 5) + h) ^ str.charCodeAt(--i);
    return (h >>> 0).toString(36);
  }

  // ── 4. TRAFFIC CLASSIFICATION ────────────────────────────────────────────────
  //
  // Priority: click ID → utm_medium → utm_source → document.referrer domain.

  function classifyTraffic(touch, ref) {
    if (touch.gclid  || touch.wbraid || touch.gbraid) return 'paid_search_google';
    if (touch.fbclid)                                 return 'paid_social_meta';
    if (touch.msclkid)                                return 'paid_search_bing';
    if (touch.ttclid)                                 return 'paid_social_tiktok';
    if (touch.twclid)                                 return 'paid_social_twitter';
    if (touch.li_fat_id)                              return 'paid_social_linkedin';
    if (touch.sccid)                                  return 'paid_social_snapchat';
    if (touch.irclickid)                              return 'affiliate';
    if (touch.mc_eid)                                 return 'email';

    var med = (touch.utm_medium || '').toLowerCase().trim();
    var src = (touch.utm_source || '').toLowerCase().trim();

    if (med) {
      if (/^(cpc|ppc|paid.?search|paidsearch|paid_search|search_paid)$/.test(med))  return 'paid_search';
      if (/^(paid.?social|paidsocial|paid_social|social.?paid|cpv|cpm)$/.test(med)) return 'paid_social';
      if (/^(display|banner|retargeting|remarketing|programmatic)$/.test(med))      return 'display';
      if (/^(email|newsletter|crm|edm|drip|lifecycle)$/.test(med))                  return 'email';
      if (/^(social|social.?media|organic.?social)$/.test(med))                    return 'organic_social';
      if (/^(affiliate|partner|partner.?referral)$/.test(med))                      return 'affiliate';
      if (/^(push|web.?push|notification|browser.?push)$/.test(med))                return 'push';
      if (/^(sms|whatsapp|messaging|waba)$/.test(med))                              return 'messaging';
      if (/^(organic|seo|natural|seo.?organic)$/.test(med))                         return 'organic_search';
      if (/^(video|youtube|yt)$/.test(med))                                         return 'video';
      if (/^(referral)$/.test(med))                                                 return 'referral';
    }

    if (src) {
      if (/^(google|googleads|google_ads)$/.test(src))                              return 'organic_search';
      if (/^(bing|microsoft|msn)$/.test(src))                                       return 'organic_search';
      if (/^(facebook|instagram|meta|fb)$/.test(src))                               return 'organic_social';
      if (/^(youtube|yt)$/.test(src))                                               return 'organic_social';
      if (/^(twitter|x\.com|x)$/.test(src))                                         return 'organic_social';
      if (/^(linkedin|li)$/.test(src))                                              return 'organic_social';
      if (/^(whatsapp|wa)$/.test(src))                                              return 'messaging';
      if (/^(email|newsletter|mailchimp|klaviyo|clevertap|leadsquared|moengage)$/.test(src)) return 'email';
      if (/^(sharechat|moj|josh)$/.test(src))                                       return 'organic_social';
    }

    return classifyReferrer(ref);
  }

  // Pure referrer-domain classification — always-on capture path (no UTMs present).
  function classifyReferrer(ref) {
    if (!ref) return 'direct';

    var host;
    try { host = new URL(ref).hostname.replace(/^www\./, '').toLowerCase(); }
    catch (e) { return 'unknown'; }

    if (host === OWN_HOST || host.endsWith('.' + OWN_HOST)) return 'internal';

    if (/google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|yandex\./.test(host)) return 'organic_search';

    if (/facebook\.com|fb\.com/.test(host))            return 'organic_social_facebook';
    if (/instagram\.com|l\.instagram\.com/.test(host)) return 'organic_social_instagram';
    if (/youtube\.com|youtu\.be/.test(host))           return 'organic_social_youtube';
    if (/twitter\.com|t\.co|x\.com/.test(host))        return 'organic_social_twitter';
    if (/linkedin\.com/.test(host))                    return 'organic_social_linkedin';
    if (/pinterest\.com|pin\.it/.test(host))           return 'organic_social_pinterest';
    if (/snapchat\.com/.test(host))                    return 'organic_social_snapchat';
    if (/whatsapp\.com|wa\.me/.test(host))             return 'organic_social_whatsapp';
    if (/sharechat\.com|moj\.live/.test(host))         return 'organic_social_sharechat';
    if (/quora\.com/.test(host))                       return 'organic_social_quora';
    if (/threads\.net/.test(host))                     return 'organic_social_threads';

    if (/amazon\.in|flipkart\.com|myntra\.com|nykaa\.com|meesho\.com|jiomart\.com/.test(host)) return 'marketplace';

    if (/mail\.google\.com|outlook\.live|yahoo\.com\/mail/.test(host)) return 'email';

    return 'referral';
  }

  // ── 5. QUERY-PARAM HELPERS ───────────────────────────────────────────────────

  // Every query param on the current URL as a plain object (last value wins on dupes).
  function readAllQueryParams() {
    var out = {};
    try {
      var params = new URLSearchParams(location.search);
      params.forEach(function (val, key) {
        if (key) out[key] = String(val).slice(0, MAX_QUERY_VAL);
      });
    } catch (e) {}
    return out;
  }

  // ── 6. URL PARAM CAPTURE (UTM / click IDs) ───────────────────────────────────

  function readTouchFromUrl() {
    if (!location.search) return null;   // fast path: no query string → nothing to capture

    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return null; }

    var touch = {}, found = false;
    for (var i = 0; i < ATTR_KEYS.length; i++) {
      var rawKey = ATTR_KEYS[i];
      // canonical / lowercase / uppercase — handles tools that mangle UTM case.
      var v = params.get(rawKey) ||
              params.get(rawKey.toLowerCase()) ||
              params.get(rawKey.toUpperCase());
      if (v) {
        touch[NORM_KEYS[i]] = String(v).slice(0, MAX_LEN);
        found = true;
      }
    }
    if (!found) return null;

    touch.landing_page = String(location.pathname + location.search).slice(0, MAX_LEN);
    var ref = document.referrer || '';
    if (ref) touch.referrer = ref.slice(0, MAX_LEN);
    touch.ts           = new Date().toISOString();
    touch.traffic_type = classifyTraffic(touch, ref);
    return touch;
  }

  function captureOnLanding() {
    var touch = readTouchFromUrl();
    if (!touch) return;

    var json = JSON.stringify(touch);
    writeStore(C_LAST, json);
    if (!readStore(C_FIRST)) writeStore(C_FIRST, json);
  }

  // ── 7. ALWAYS-ON REFERRER CAPTURE ────────────────────────────────────────────
  //
  // Runs on every page load. Internal navigation is skipped so it never
  // overwrites the real external referrer.

  function captureReferrerAlways() {
    var ref  = document.referrer || '';
    var page = String(location.pathname + location.search).slice(0, MAX_LEN);

    var refHost = '';
    if (ref) {
      try { refHost = new URL(ref).hostname.replace(/^www\./, '').toLowerCase(); }
      catch (e) {}
    }
    var isInternal = refHost === OWN_HOST || refHost.endsWith('.' + OWN_HOST);
    if (isInternal) return;

    var refData = {
      referrer     : ref.slice(0, MAX_LEN),
      landing_page : page,
      traffic_type : classifyReferrer(ref),
      ts           : new Date().toISOString()
    };
    var json = JSON.stringify(refData);

    writeStore(C_RF_L, json);
    if (!readStore(C_RF_F)) writeStore(C_RF_F, json);
  }

  // ── 8. ALWAYS-ON LANDING CAPTURE (classification-independent) ────────────────
  //
  // The catch-all. Runs once per session and snapshots the ENTRY page — full
  // path, raw query string, and every query parameter — no matter whether the
  // traffic can be classified. This is what guarantees that a `direct` /
  // `referral` / `unknown` visit STILL records where the user landed and through
  // what params (affiliate subids, coupons, custom click refs, etc.).
  //
  // Gated on a per-session flag so internal navigation within the session does
  // not overwrite the true entry page. localStorage-only by design (keeps cookie
  // headers lean; last_landing_page/first_landing_page remain the cookie fallback).

  function captureLanding() {
    if (ssGet(S_LAND)) return;   // entry page already snapshotted this session
    ssSet(S_LAND, '1');

    var snap = {
      path   : location.pathname.slice(0, MAX_LEN),
      query  : location.search.replace(/^\?/, '').slice(0, MAX_QUERY_VAL),
      params : readAllQueryParams(),
      ref    : (document.referrer || '').slice(0, MAX_LEN),
      ts     : new Date().toISOString()
    };
    var json = JSON.stringify(snap);

    lsSet(C_LAND_L, json);
    if (!lsGet(C_LAND_F)) lsSet(C_LAND_F, json);
  }

  // Fan out UNKNOWN query params (anything not already captured as utm_*/click ID)
  // as discrete `qp_<name>` attributes — capped and with sensitive keys denylisted.
  function emitUnknownParams(target, params) {
    if (!params) return;

    var count = 0;
    for (var raw in params) {
      if (!Object.prototype.hasOwnProperty.call(params, raw)) continue;
      if (count >= MAX_QP) break;

      var nk = normKey(raw).slice(0, MAX_QP_KEY);
      if (!nk || KNOWN_KEYS[nk]) continue;        // skip blanks + already-captured keys
      if (SENSITIVE_QP.test(nk)) continue;        // never write PII / secrets (normalised key
                                                  // catches hyphenated variants, e.g. e-mail)

      // Query-param values are NOT truncated to 255 — only bounded by MAX_QUERY_VAL.
      var val = String(params[raw]).slice(0, MAX_QUERY_VAL);
      if (!val) continue;

      target['qp_' + nk] = val;
      count++;
    }
  }

  // ── 9. VISIT COUNTER ─────────────────────────────────────────────────────────

  function trackVisit() {
    var data = parse(lsGet(C_VISITS)) || { count: 0, first: new Date().toISOString() };
    if (!ssGet(S_SESS)) {
      data.count += 1;
      data.last = new Date().toISOString();
      ssSet(S_SESS, '1');
      lsSet(C_VISITS, JSON.stringify(data));
    }
    return data;
  }

  // ── 10. BUILD CART ATTRIBUTES ────────────────────────────────────────────────

  function buildAttributes() {
    var last   = parse(readStore(C_LAST));
    var first  = parse(readStore(C_FIRST));
    var refL   = parse(readStore(C_RF_L));
    var refF   = parse(readStore(C_RF_F));
    var landL  = parse(lsGet(C_LAND_L));
    var landF  = parse(lsGet(C_LAND_F));
    var visits = parse(lsGet(C_VISITS));

    if (!last && !first && !refL && !refF && !landL && !landF) return null;

    var a = {}, i, nk;

    // ── Last-touch UTM / click IDs (unprefixed)
    if (last) {
      for (i = 0; i < NORM_KEYS.length; i++) {
        nk = NORM_KEYS[i];
        if (last[nk]) a[nk] = last[nk];
      }
      if (last.landing_page) a.last_landing_page = last.landing_page;
      if (last.referrer)     a.referrer          = last.referrer;
      if (last.referrer)     a.last_referrer      = last.referrer;
      if (last.traffic_type) a.traffic_type       = last.traffic_type;
      if (last.traffic_type) a.last_traffic_type  = last.traffic_type;
      if (last.ts)           a.last_touch_ts       = last.ts;
    }

    // ── First-touch UTM / click IDs (prefixed)
    if (first) {
      for (i = 0; i < NORM_KEYS.length; i++) {
        nk = NORM_KEYS[i];
        if (first[nk]) a['first_' + nk] = first[nk];
      }
      if (first.landing_page) a.first_landing_page = first.landing_page;
      if (first.referrer)     a.first_referrer      = first.referrer;
      if (first.traffic_type) a.first_traffic_type  = first.traffic_type;
      if (first.ts)           a.first_touch_ts       = first.ts;
    }

    // ── Always-on referrer — fills gaps for organic / direct / untagged visits.
    if (refL) {
      if (!a.referrer)          a.referrer          = refL.referrer;
      if (!a.last_referrer)     a.last_referrer      = refL.referrer;
      if (!a.last_landing_page) a.last_landing_page  = refL.landing_page;
      if (!a.traffic_type)      a.traffic_type       = refL.traffic_type;
      if (!a.last_traffic_type) a.last_traffic_type  = refL.traffic_type;
      if (!a.last_touch_ts)     a.last_touch_ts      = refL.ts;
    }
    if (refF) {
      if (!a.first_referrer)     a.first_referrer     = refF.referrer;
      if (!a.first_landing_page) a.first_landing_page = refF.landing_page;
      if (!a.first_traffic_type) a.first_traffic_type = refF.traffic_type;
      if (!a.first_touch_ts)     a.first_touch_ts     = refF.ts;
    }

    // ── Landing snapshot (always present) — the catch-all signal. Records the
    //    entry page + raw query + every unknown param, even when the channel is
    //    direct / referral / unknown.
    if (landL) {
      if (landL.path)  a.landing_path  = landL.path;
      if (landL.query) a.landing_query = landL.query;
      emitUnknownParams(a, landL.params);
      // Backfill last_landing_page if neither UTM nor referrer set it.
      if (!a.last_landing_page) {
        a.last_landing_page = (landL.path + (landL.query ? '?' + landL.query : '')).slice(0, MAX_LEN);
      }
    }
    if (landF) {
      if (landF.path)  a.first_landing_path  = landF.path;
      if (landF.query) a.first_landing_query = landF.query;
      if (!a.first_landing_page) {
        a.first_landing_page = (landF.path + (landF.query ? '?' + landF.query : '')).slice(0, MAX_LEN);
      }
    }

    // ── Visit metadata
    if (visits) {
      a.visit_count   = String(visits.count || 1);
      a.customer_type = (visits.count <= 1) ? 'new_visitor' : 'returning_visitor';
      if (visits.first) a.first_visit_ts = visits.first;
    }

    return Object.keys(a).length ? a : null;
  }

  // ── 11. SMART CART SYNC ──────────────────────────────────────────────────────
  //
  // Hash-gated: writes only when the attribute payload changes.
  // force=true bypasses the hash (pre-checkout safety net) and sets keepalive so
  // the POST survives the navigation to checkout.

  var syncing = false; // in-flight guard against duplicate background POSTs

  function syncToCart(force) {
    // Background syncs de-dupe; forced (pre-checkout) syncs always proceed.
    if (syncing && !force) return;

    var attrs = buildAttributes();
    if (!attrs) return;

    var h = hashStr(JSON.stringify(attrs));
    if (!force && ssGet(S_HASH) === h) return; // nothing changed — skip

    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var cfg = (typeof fetchConfig === 'function')
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    cfg.body = JSON.stringify({ attributes: attrs });
    if (force) cfg.keepalive = true; // survive the checkout navigation

    if (!force) syncing = true;
    fetch(url, cfg)
      .then(function (r) { if (r && r.ok) ssSet(S_HASH, h); })
      .catch(function () { /* never block the UI on attribution */ })
      .finally(function () { if (!force) syncing = false; });
  }

  // ── 12. PRE-CHECKOUT SYNC GUARD ──────────────────────────────────────────────

  function wireCheckoutGuard() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, function () { syncToCart(); });
      if (PUB_SUB_EVENTS.openCart)   subscribe(PUB_SUB_EVENTS.openCart,   function () { syncToCart(); });
    }

    // Single upward DOM walk per click. One closest() against a selector list
    // tests each ancestor once — far cheaper than 5 separate closest() calls.
    // [name="checkout"] and a[href*="/checkout"] also match when the click target
    // IS the button/anchor (closest includes the element itself).
    var CHECKOUT_SEL = '[name="checkout"], a[href*="/checkout"], [data-action="checkout"], .cart__checkout, .cart-checkout';
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest(CHECKOUT_SEL)) syncToCart(true);
    }, true);

    document.addEventListener('submit', function (e) {
      if (e.target && e.target.action && e.target.action.indexOf('/checkout') > -1) {
        syncToCart(true);
      }
    }, true);

    // Razorpay Magic Checkout — verify the real event name (see audit action items).
    document.addEventListener('rzp:checkout_open',   function () { syncToCart(true); });
    document.addEventListener('rzp-checkout-opened', function () { syncToCart(true); });
    document.addEventListener('rzp.payment.open',    function () { syncToCart(true); });
  }

  // ── 13. WIRE UP ──────────────────────────────────────────────────────────────

  captureOnLanding();        // UTM / click ID capture from URL
  captureReferrerAlways();   // Always-on referrer capture (non-UTM visits)
  captureLanding();          // Always-on landing + query capture (catch-all)
  trackVisit();              // Visit counter
  wireCheckoutGuard();       // Pre-checkout sync listeners

  function scheduleSync() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { syncToCart(); }, { timeout: 2000 });
    } else {
      setTimeout(function () { syncToCart(); }, 1200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }

})();
